import { useState } from "react";
import { usePageMeta } from "@/lib/seo";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { PromoCodeField } from "@/components/PromoCodeField";

const BIZ_TYPES = ["auto_repair", "collision", "dealership", "contractor", "service", "other"];

export default function QuotePage() {
  usePageMeta("Get a Quote", "Request a custom quote for The ShopAdvisor™ software and website growth services.");
  const [form, setForm] = useState({ name: "", email: "", phone: "", business_name: "", business_type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [promo, setPromo] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/api/quote-requests", { ...form, promo_code: promo?.code || "" });
      toast.success("Quote request submitted! Our team will prepare a custom quote for you.");
      setForm({ name: "", email: "", phone: "", business_name: "", business_type: "", message: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to submit."); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-20 px-6 md:px-12 lg:px-24" data-testid="quote-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-blue-600 mb-3">Custom Quote</p>
          <h1 className="text-4xl sm:text-5xl tracking-tighter font-black text-slate-900 mb-4">Request a Quote</h1>
          <p className="text-slate-500">Get custom pricing tailored to your specific business needs.</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm space-y-5" data-testid="quote-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div><Label className="text-slate-700 text-sm mb-1.5 block">Full Name</Label><Input className="bg-white border-slate-300 text-slate-900" placeholder="John Smith" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="quote-name-input" /></div>
            <div><Label className="text-slate-700 text-sm mb-1.5 block">Email</Label><Input type="email" className="bg-white border-slate-300 text-slate-900" placeholder="john@shop.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required data-testid="quote-email-input" /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div><Label className="text-slate-700 text-sm mb-1.5 block">Phone</Label><Input className="bg-white border-slate-300 text-slate-900" placeholder="(555) 000-0000" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required data-testid="quote-phone-input" /></div>
            <div><Label className="text-slate-700 text-sm mb-1.5 block">Business Name</Label><Input className="bg-white border-slate-300 text-slate-900" placeholder="Smith's Auto" value={form.business_name} onChange={e => setForm({...form, business_name: e.target.value})} required data-testid="quote-business-input" /></div>
          </div>
          <div>
            <Label className="text-slate-700 text-sm mb-1.5 block">Business Type</Label>
            <Select value={form.business_type} onValueChange={v => setForm({...form, business_type: v})}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="quote-biztype-select"><SelectValue placeholder="Select your business type" /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200">{BIZ_TYPES.map(t => <SelectItem key={t} value={t} className="text-slate-900">{t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-700 text-sm mb-1.5 block">Tell us about your needs</Label>
            <Textarea className="bg-white border-slate-300 text-slate-900 min-h-[120px]" placeholder="Describe your business, number of locations, team size..." value={form.message} onChange={e => setForm({...form, message: e.target.value})} data-testid="quote-message-input" />
          </div>
          <div>
            <Label className="text-slate-700 text-sm mb-1.5 block">Promo code (optional)</Label>
            <PromoCodeField amount={100} onChange={setPromo} testIdPrefix="quote-promo" />
          </div>
          <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-lg transition-colors disabled:opacity-50" data-testid="quote-submit-btn">
            {loading ? "Submitting..." : "Request Custom Quote"}
          </button>
        </form>
      </div>
    </div>
  );
}
